import {
  Box,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogActions,
  DialogTitle
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { OrderContext } from 'contexts/OrderContextProvider'
import { useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { isObjectNotEmpty } from 'utils'
import { OperationType } from 'constant'
import HeaderDialogContent from './HeaderDialogContent'
import useDailyOrder from 'hooks/useDailyOrder'
import {
  createOperationConfigs,
  getOperationConfigByType
} from './operationConfigs'

interface Props {
  label?: string
  buttonLink?: string
  operationType?: OperationType
}

const Header = ({ label, buttonLink, operationType }: Props) => {
  const navigate = useNavigate()
  const {
    setOrderContents,
    setOrderSearchError,
    orderContents,
    products,
    handleSubmitPickingSalesOrder,
    handleSubmitPurchaseOrderReceiving,
    setOrderNumber,
    inputRef,
    setItemSearchError,
    handleSubmitPickingTransferOrder,
    handleSubmitScanningTOFBALabel,
    orderNumber,
    company
  } = useContext(OrderContext)
  const { removeCurrentUserCache } = useDailyOrder()
  const [openDialog, setOpenDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const operationConfigs = useMemo(
    () =>
      createOperationConfigs({
        handleSubmitPickingSalesOrder,
        handleSubmitPurchaseOrderReceiving,
        handleSubmitPickingTransferOrder,
        handleSubmitScanningTOFBALabel
      }),
    [
      handleSubmitPickingSalesOrder,
      handleSubmitPurchaseOrderReceiving,
      handleSubmitPickingTransferOrder,
      handleSubmitScanningTOFBALabel
    ]
  )

  const currentOperation = useMemo(() => {
    if (!operationType) {
      return undefined
    }
    return getOperationConfigByType(operationConfigs, operationType)
  }, [operationConfigs, operationType])

  const isSubmitDisabled = useMemo(
    () =>
      currentOperation ? currentOperation.calculateDisabled(products) : true,
    [currentOperation, products]
  )

  useEffect(() => {
    inputRef?.current?.focus()
  }, [openDialog])

  return (
    <Box sx={{ backgroundColor: '#607998', padding: '5px' }}>
      <Grid
        container
        sx={{
          '& button, button:focus, button:active': {
            backgroundColor: '#24385b',
            color: '#ffffff'
          }
        }}
      >
        <Grid size='grow'>
          {isObjectNotEmpty(orderContents) ? (
            <Button
              variant='contained'
              onClick={async () => {
                removeCurrentUserCache(orderNumber, company)
                setOrderContents({})
                setOrderNumber('')
                setItemSearchError('')
              }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              variant='contained'
              onClick={() => {
                if (buttonLink) navigate(buttonLink)
                else navigate(-1)
                setOrderSearchError(false)
                setOrderNumber('')
              }}
            >
              Back
            </Button>
          )}
        </Grid>
        <Grid size={6} sx={{ textAlign: 'center' }}>
          <Typography
            sx={{ color: '#ffffff', fontWeight: 600, lineHeight: '36px' }}
          >
            {label}
          </Typography>
        </Grid>
        <Grid
          size='grow'
          sx={{
            '& .Mui-disabled': {
              color: '#A9A9A9'
            },
            textAlign: 'right'
          }}
        >
          {isObjectNotEmpty(orderContents) && (
            <Button
              onClick={() => {
                setOpenDialog(true)
              }}
              disabled={isSubmitDisabled}
            >
              Submit
            </Button>
          )}
        </Grid>
      </Grid>
      <Dialog open={openDialog}>
        <DialogTitle>Confirmation</DialogTitle>
        {currentOperation && (
          <HeaderDialogContent
            firstLine={currentOperation.dialogContent.firstLine(products)}
            secondLine={currentOperation.dialogContent.secondLine(products)}
          />
        )}
        <DialogActions>
          <Button
            onClick={() => {
              setLoading(false)
              setOpenDialog(false)
            }}
          >
            Disagree
          </Button>
          <LoadingButton
            loading={loading}
            variant='outlined'
            onClick={() => {
              if (currentOperation) {
                currentOperation.submitHandler(setOpenDialog, setLoading)
              }
            }}
          >
            Agree
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Header
